import { openDB } from 'idb';

const DB_NAME = 'WriteFormDB';
const DB_VERSION = 1;
const STORE_NAME = 'documents';

class IndexedDBService {
  constructor() {
    this.db = null;
  }

  // Initialize the database
  async init() {
    if (this.db) return this.db;
    
    this.db = await openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Create the documents store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          // Create indexes for efficient querying
          store.createIndex('lastSynced', 'lastSynced', { unique: false });
          store.createIndex('updatedAt', 'updatedAt', { unique: false });
        }
      },
    });
    
    return this.db;
  }

  // Store a document in IndexedDB
  async storeDocument(document) {
    const db = await this.init();
    
    const documentData = {
      id: document._id || document.id,
      ...document,
      lastSynced: new Date().toISOString(),
      storedAt: new Date().toISOString()
    };

    await db.put(STORE_NAME, documentData);
    return documentData;
  }

  // Get a document from IndexedDB by ID
  async getDocument(documentId) {
    const db = await this.init();
    return await db.get(STORE_NAME, documentId);
  }

  // Get all documents from IndexedDB
  async getAllDocuments() {
    const db = await this.init();
    return await db.getAll(STORE_NAME);
  }

  // Update document fileContent in IndexedDB
  async updateDocumentContent(documentId, fileContent) {
    const db = await this.init();
    const document = await db.get(STORE_NAME, documentId);
    
    if (document) {
      document.fileContent = fileContent;
      document.lastModified = new Date().toISOString();
      await db.put(STORE_NAME, document);
      return document;
    }
    
    throw new Error('Document not found in IndexedDB');
  }

  // Update document metadata (title, status, etc.)
  async updateDocumentMetadata(documentId, metadata) {
    const db = await this.init();
    const document = await db.get(STORE_NAME, documentId);
    
    if (document) {
      Object.assign(document, metadata);
      document.lastModified = new Date().toISOString();
      await db.put(STORE_NAME, document);
      return document;
    }
    
    throw new Error('Document not found in IndexedDB');
  }

  // Replace current document with new document (for switching documents)
  async replaceCurrentDocument(newDocument) {
    const db = await this.init();
    
    // Clear all existing documents (since we only store one at a time)
    await db.clear(STORE_NAME);
    
    // Store the new document
    return await this.storeDocument(newDocument);
  }

  // Get the last synced timestamp for a document
  async getLastSynced(documentId) {
    const document = await this.getDocument(documentId);
    return document?.lastSynced || null;
  }

  // Update the last synced timestamp
  async updateLastSynced(documentId, timestamp = null) {
    const db = await this.init();
    const document = await db.get(STORE_NAME, documentId);
    
    if (document) {
      document.lastSynced = timestamp || new Date().toISOString();
      await db.put(STORE_NAME, document);
      return document;
    }
    
    throw new Error('Document not found in IndexedDB');
  }

  // Check if document exists in IndexedDB
  async hasDocument(documentId) {
    const document = await this.getDocument(documentId);
    return !!document;
  }

  // Delete a document from IndexedDB
  async deleteDocument(documentId) {
    const db = await this.init();
    await db.delete(STORE_NAME, documentId);
  }

  // Clear all documents from IndexedDB
  async clearAllDocuments() {
    const db = await this.init();
    await db.clear(STORE_NAME);
  }

  // Get document count
  async getDocumentCount() {
    const db = await this.init();
    return await db.count(STORE_NAME);
  }

  // Check if IndexedDB is supported
  static isSupported() {
    return 'indexedDB' in window;
  }
}

// Create a singleton instance
const indexedDBService = new IndexedDBService();

export default indexedDBService;
